import { useEffect, useState } from 'react';
import { api } from '../../api';
import type { Chefe, EventoAtual } from '../../api/types';
import type { Penta } from '../../api/evento';
import { CORES_PENTA } from '../../world/pentagrama';
import { t } from '../../i18n/idioma';
import { nomeDoMapa } from '../../i18n/nomes';
import { BotaoPerigoso, type Rodar } from './comum';

interface Props {
  busy: boolean;
  rodar: Rodar;
  evAtual: EventoAtual | null;
  chefe: Chefe | null;
  penta: Penta | null;
}

/** Eventos: o Ultimate Battle, o Chefe do Mundo e o Pentagrama lado a lado, cada um com o estado, os campos e as ações. */
export function EventosGm({ busy, rodar, evAtual, chefe, penta }: Props) {
  const [taxa, setTaxa] = useState('50000');
  const [inscricao, setInscricao] = useState('5');
  const [partida, setPartida] = useState('15');
  const [daemons, setDaemons] = useState('3');
  const [modo, setModo] = useState('trajeDaCasa');
  const [variante, setVariante] = useState('');
  const [vida, setVida] = useState('');
  const [duracao, setDuracao] = useState('');
  const [corPenta, setCorPenta] = useState('');
  useEffect(() => { if (evAtual?.taxa) setTaxa(String(evAtual.taxa)); }, [evAtual?.taxa]);

  const ubAberto = !!evAtual && evAtual.estado !== 0;
  const chefeVivo = !!(chefe?.aoVivo && chefe.aoVivo.estado === 0);
  const pentaVivo = !!penta?.aoVivo;
  const num = (v: string) => Number(v) || undefined;

  return (
    <div className="gmx-grade gmx-g2">
      <div className="gmx-card">
        <div className="gmx-linha">
          <p className="gmx-h" style={{ margin: 0 }}>{t('evento.nome')}</p>
          <span className="gmx-esp" />
          <span className="gmx-meta">
            <i className="gmx-dot" style={{ background: ubAberto ? 'var(--bad)' : 'var(--ink-dim)', marginRight: 6 }} />
            {ubAberto ? t('evento.gm.inscricoes') : t('evento.gm.fechado')}
            {ubAberto && <> · {t('evento.inscritos', { n: evAtual!.inscritos })} · {t('evento.gm.pares', { n: evAtual!.pares })}</>}
          </span>
        </div>
        <div className="gmx-campos">
          <label>{t('evento.gm.taxa')}<input value={taxa} onChange={e => setTaxa(e.target.value)} inputMode="numeric" disabled={ubAberto} /></label>
          <label>{t('evento.gm.modo')}
            <select value={modo} onChange={e => setModo(e.target.value)} disabled={ubAberto}>
              <option value="trajeDaCasa">{t('evento.modo.casa')}</option>
              <option value="trajeProprio">{t('evento.modo.proprio')}</option>
            </select>
          </label>
          <label>{t('evento.gm.inscricao')}<input value={inscricao} onChange={e => setInscricao(e.target.value)} inputMode="numeric" disabled={ubAberto} /></label>
          <label>{t('evento.gm.partida')}<input value={partida} onChange={e => setPartida(e.target.value)} inputMode="numeric" disabled={ubAberto} /></label>
          <label>{t('evento.gm.daemons')}<input value={daemons} onChange={e => setDaemons(e.target.value)} inputMode="numeric" disabled={ubAberto} /></label>
        </div>
        <div className="gmx-linha">
          <span className="gmx-esp" />
          {ubAberto
            ? <BotaoPerigoso rotulo={t('evento.gm.cancelar')} confirmar={t('gm.x.confirmarCancelarUb')} disabled={busy}
                onConfirm={() => void rodar(() => api.eventoCancelar(), { cat: t('gm.x.cat.evento'), txt: t('gm.x.reg.cancelouUb') })} />
            : <button type="button" className="btn btn--primary" disabled={busy}
                onClick={() => void rodar(() => api.eventoAbrir({ taxa: num(taxa), inscricaoMin: num(inscricao), partidaMin: num(partida), daemonMin: num(daemons), modo }),
                  { cat: t('gm.x.cat.evento'), txt: t('gm.x.reg.abriuUb') })}>
                {t('evento.gm.abrir')}
              </button>}
        </div>
      </div>

      <div className="gmx-card">
        <div className="gmx-linha">
          <p className="gmx-h" style={{ margin: 0 }}>{t('chefe.nome')}</p>
          <span className="gmx-esp" />
          <span className="gmx-meta">
            <i className="gmx-dot" style={{ background: chefeVivo ? 'var(--bad)' : 'var(--ink-dim)', marginRight: 6 }} />
            {chefeVivo
              ? <>{chefe!.aoVivo!.nome} · {nomeDoMapa(chefe!.aoVivo!.mapa)} · {Math.ceil(chefe!.aoVivo!.permil / 10)}% · {t('chefe.barra.lutando', { n: chefe!.aoVivo!.lutadores })}</>
              : <>{t('chefe.gm.nenhum')}{chefe?.proximos[0] && <> · {t('chefe.gm.proximo', { hora: chefe.proximos[0].hora })}</>}</>}
          </span>
        </div>
        {chefeVivo && <span className="gm-chefe-vivo__bar" style={{ display: 'block', marginTop: 6 }}><i style={{ width: `${chefe!.aoVivo!.permil / 10}%` }} /></span>}
        <div className="gmx-campos">
          <label style={{ gridColumn: '1 / -1' }}>{t('chefe.gm.cor')}
            <select value={variante} onChange={e => setVariante(e.target.value)} disabled={chefeVivo}>
              <option value="">{t('chefe.gm.aoAcaso')}</option>
              {(chefe?.variantes ?? []).map(v => (
                <option key={v.id} value={v.id}>{v.nome} · {nomeDoMapa(v.mapa)}{v.ativa ? '' : ` · ${t('chefe.altar.foraRotacao')}`}</option>
              ))}
            </select>
          </label>
          <label>{t('chefe.gm.vida')}<input value={vida} onChange={e => setVida(e.target.value)} inputMode="numeric" disabled={chefeVivo}
            placeholder={chefe ? String(chefe.regras.vidaBase) : ''} /></label>
          <label>{t('chefe.gm.duracao')}<input value={duracao} onChange={e => setDuracao(e.target.value)} inputMode="numeric" disabled={chefeVivo}
            placeholder={chefe ? String(Math.round(chefe.regras.duracaoSegundos / 60)) : ''} /></label>
        </div>
        <p className="gmx-meta">{t('chefe.gm.dica')}</p>
        <div className="gmx-linha">
          <span className="gmx-esp" />
          {chefeVivo
            ? <BotaoPerigoso rotulo={t('chefe.gm.encerrar')} confirmar={t('gm.x.confirmarEncerrarChefe')} disabled={busy}
                onConfirm={() => void rodar(() => api.eventoChefeEncerrar(), { cat: t('gm.x.cat.evento'), txt: t('gm.x.reg.encerrouChefe') })} />
            : <button type="button" className="btn btn--primary" disabled={busy}
                onClick={() => void rodar(() => api.eventoChefeIniciar({ variante: variante || undefined, vida: num(vida), duracaoMin: num(duracao) }),
                  { cat: t('gm.x.cat.evento'), txt: t('gm.x.reg.iniciouChefe') })}>
                {t('chefe.gm.iniciar')}
              </button>}
        </div>
      </div>
      <div className="gmx-card">
        <div className="gmx-linha">
          <p className="gmx-h" style={{ margin: 0 }}>{t('penta.nome')}</p>
          <span className="gmx-esp" />
          <span className="gmx-meta">
            <i className="gmx-dot" style={{ background: pentaVivo ? 'var(--bad)' : 'var(--ink-dim)', marginRight: 6 }} />
            {pentaVivo
              ? <>{t(`penta.ponta.${penta!.aoVivo!.cor}`)} · {t('chefe.barra.lutando', { n: penta!.aoVivo!.lutadores })}{penta!.aoVivo!.fase > 0 && <> · {t(`penta.guardiao.${penta!.aoVivo!.cor}`)}</>}</>
              : <>{t('penta.gm.nenhum')}{penta?.proximos[0] && <> · {t('penta.gm.proximo', { hora: penta.proximos[0].hora, ponta: t(`penta.ponta.${penta.proximos[0].cor}`) })}</>}</>}
          </span>
        </div>
        <div className="gmx-campos">
          <label style={{ gridColumn: '1 / -1' }}>{t('penta.gm.cor')}
            <select value={corPenta} onChange={e => setCorPenta(e.target.value)} disabled={pentaVivo}>
              <option value="">{t('penta.gm.daOrdem')}</option>
              {CORES_PENTA.map(c => <option key={c} value={c}>{t(`penta.ponta.${c}`)}</option>)}
            </select>
          </label>
        </div>
        <p className="gmx-meta">{t('penta.gm.dica')}</p>
        <div className="gmx-linha">
          <span className="gmx-esp" />
          {pentaVivo
            ? <BotaoPerigoso rotulo={t('penta.gm.encerrar')} confirmar={t('gm.x.confirmarEncerrarPenta')} disabled={busy}
                onConfirm={() => void rodar(() => api.eventoPentagramaEncerrar(), { cat: t('gm.x.cat.evento'), txt: t('gm.x.reg.encerrouPenta') })} />
            : <button type="button" className="btn btn--primary" disabled={busy}
                onClick={() => void rodar(() => api.eventoPentagramaIniciar(corPenta || undefined),
                  { cat: t('gm.x.cat.evento'), txt: t('gm.x.reg.iniciouPenta') })}>
                {t('penta.gm.iniciar')}
              </button>}
        </div>
      </div>
    </div>
  );
}
